import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ListTodo, Loader2, Link2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogBody as DialogBody,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogFooter as DialogFooter,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
} from "@/components/ui/responsive-dialog";
import { DatePicker } from "@/components/DatePicker";
import { MachineCombobox } from "@/components/MachineCombobox";
import { CustomerCombobox } from "@/components/report/ReportSubjectPicker";
import { PRIORITY_OPTIONS, TASK_DATE_PRESETS, createTask, updateTask, notifyTasksChanged } from "@/lib/tasks";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

const EMPTY = { title: "", description: "", priority: "normal", due_date: "", customer_id: "", machine_id: "" };

/**
 * TaskDialog – opprett/rediger eit punkt på huskelista.
 * task: null (ny) eller eksisterande punkt. defaults: {title, machine_id, customer_id} for førehandsutfylling.
 * onSaved(task) etter lagring.
 */
export const TaskDialog = ({ open, onOpenChange, task, defaults, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [machines, setMachines] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showLinks, setShowLinks] = useState(false);
  const editing = !!task?.id;

  useEffect(() => {
    if (!open) return;
    const base = task
      ? { title: task.title || "", description: task.description || "", priority: task.priority || "normal", due_date: task.due_date || "", customer_id: task.customer_id || "", machine_id: task.machine_id || "" }
      : { ...EMPTY, ...(defaults || {}) };
    setForm(base);
    setShowLinks(!!(base.customer_id || base.machine_id));
    // Kunde/maskin-lister berre når dialogen er open (små lister i pilot)
    Promise.all([api.get("/machines"), api.get("/customers")])
      .then(([m, c]) => { setMachines(m.data || []); setCustomers(c.data || []); })
      .catch(() => {});
  }, [open, task, defaults]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const onMachine = (id) => {
    const m = machines.find((x) => x.id === id);
    set({ machine_id: id || "", customer_id: m?.customer_id || form.customer_id });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const title = form.title.trim();
    if (!title) return toast.error("Skriv kva som skal hugsast.");
    setBusy(true);
    try {
      const payload = {
        title,
        description: form.description.trim(),
        priority: form.priority,
        due_date: form.due_date || "",
        customer_id: form.customer_id || "",
        machine_id: form.machine_id || "",
      };
      const saved = editing ? await updateTask(task.id, payload) : await createTask(payload);
      notifyTasksChanged();
      toast.success(editing ? "Punktet er oppdatert." : "Lagt på huskelista.");
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikkje lagre punktet.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)} snapPoints={[0.92]}>
      <DialogContent className="sm:max-w-lg" data-testid="task-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><ListTodo className="h-5 w-5 text-primary" /> {editing ? "Rediger punkt" : "Nytt punkt på huskelista"}</DialogTitle>
          <DialogDescription>Små og store jobbar som skal hugsast – utanom servicefristane. Alle på laget ser lista.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="contents" data-testid="task-form">
          <DialogBody>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Kva skal gjerast? *</Label>
              <Input className="h-12 text-base" value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="t.d. Bestille nye slangar til CAT 320" autoFocus maxLength={200} data-testid="task-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Detaljar <span className="font-normal text-muted-foreground">– valfritt</span></Label>
              <Textarea rows={3} value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="Kva, kvar, kven – det som trengst for at nokon andre kan ta jobben." maxLength={4000} data-testid="task-description-input" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold">Prioritet</Label>
                <div className="grid grid-cols-3 gap-1.5" data-testid="task-priority">
                  {PRIORITY_OPTIONS.map((o) => (
                    <button key={o.value} type="button" onClick={() => set({ priority: o.value })} aria-pressed={form.priority === o.value} data-testid={`task-priority-${o.value}`}
                      className={cn("pressable h-11 rounded-lg border px-2 text-sm font-semibold transition-colors",
                        form.priority === o.value
                          ? o.value === "hoeg" ? "border-transparent bg-primary text-[#0B0D10]" : "border-transparent bg-[#0B0D10] text-white dark:bg-white dark:text-[#0B0D10]"
                          : "bg-card hover:bg-accent")}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold">Ønska dato <span className="font-normal text-muted-foreground">– valfritt</span></Label>
                <DatePicker value={form.due_date} onChange={(d) => set({ due_date: d || "" })} presets={TASK_DATE_PRESETS} placeholder="Ingen dato" testid="task-due-date" />
              </div>
            </div>

            {!showLinks ? (
              <button type="button" onClick={() => setShowLinks(true)} className="inline-flex h-10 items-center gap-1.5 rounded-md px-2 text-sm font-semibold text-muted-foreground hover:bg-accent hover:text-foreground" data-testid="task-show-links">
                <Link2 className="h-4 w-4" /> Knyt til maskin eller kunde
              </button>
            ) : (
              <div className="space-y-3 rounded-lg border bg-muted/30 p-3" data-testid="task-links">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">Knytt til</span>
                  <button type="button" onClick={() => { setShowLinks(false); set({ machine_id: "", customer_id: "" }); }} className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-muted-foreground hover:bg-accent hover:text-foreground" data-testid="task-clear-links"><X className="h-3.5 w-3.5" /> Fjern kopling</button>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-muted-foreground">Maskin</Label>
                  <MachineCombobox machines={machines} value={form.machine_id} onChange={onMachine} placeholder="Vel maskin (valfritt)" testid="task-machine" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-muted-foreground">Kunde</Label>
                  <CustomerCombobox customers={customers} value={form.customer_id} onChange={(id) => set({ customer_id: id || "" })} placeholder="Vel kunde (valfritt)" testid="task-customer" />
                </div>
              </div>
            )}
          </DialogBody>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid="task-cancel">Avbryt</Button>
            <Button type="submit" disabled={busy || !form.title.trim()} data-testid="task-save">{busy && <Loader2 className="animate-spin" />} {editing ? "Lagre" : "Legg på lista"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
